import { useAuth } from "../components/context/LoginContext/AuthContext";
import { useCallback } from "react";
import { useAxios } from "./useAxios";

/**
 * useSchoolParentApi
 * ------------------
 * Reusable API helper for School Parent related APIs.
 * Handles:
 *  - Token injection from context / localStorage
 *  - Main domain → fetchWithAuth
 *  - Subdomain / cross-origin → axiosInstance
 *  - Error handling
 */
export const useSchoolParentApi = () => {
  const { user, fetchWithAuth } = useAuth();
  const { axiosInstance } = useAxios();

  // Helper to check if URL is same-origin (main domain)
  const isSameOrigin = (url) => {
    try {
      return new URL(url).origin === window.location.origin;
    } catch {
      return false;
    }
  };

  // Main API call function
  const apiCall = useCallback(
    async (url, options = {}) => {
      const method = options.method || "GET";
      const headers = {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      };

      // Attach token
      const token = user?.token || localStorage.getItem("user_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;

      try {
        if (isSameOrigin(url)) {
          // ✅ Main domain → fetchWithAuth
          if (!fetchWithAuth) throw new Error("fetchWithAuth not available");

          const res = await fetchWithAuth(url, {
            method,
            headers,
            body: options.body,
          });

          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(errorText || res.statusText);
          }

          return res.json();
        } else {
          // ✅ Subdomain / cross-origin → axiosInstance
          const res = await axiosInstance({
            url,
            method,
            headers,
            data: options.body ? JSON.parse(options.body) : undefined,
          });

          return res.data;
        }
      } catch (err) {
        console.error("useSchoolParentApi Error:", err);
        throw err;
      }
    },
    [user, fetchWithAuth, axiosInstance],
  );

  return { apiCall };
};
